import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { BackgroundExportStatus, ExportStatus } from '@/utils/organization/backgroundExport';

interface UseRealtimeExportStatusOptions {
  exportId: string;
  onStatusChange?: (status: BackgroundExportStatus) => void;
  fallbackPollingInterval?: number;
  maxMonitoringDuration?: number; // Stop monitoring after this duration (ms)
}

// Global connection registry to prevent duplicate connections
const connectionRegistry = new Map<string, {
  count: number;
  channel: any;
  callbacks: ((status: BackgroundExportStatus) => void)[];
}>();

export const useRealtimeExportStatus = ({
  exportId,
  onStatusChange,
  fallbackPollingInterval = 5000,
  maxMonitoringDuration = 30 * 60 * 1000 // 30 minutes max monitoring
}: UseRealtimeExportStatusOptions) => {
  const [status, setStatus] = useState<BackgroundExportStatus | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMonitoringActive, setIsMonitoringActive] = useState(true);
  const startTimeRef = useRef<number>(Date.now());
  const pollIntervalRef = useRef<NodeJS.Timeout>();
  const onStatusChangeRef = useRef<typeof onStatusChange>(onStatusChange);

  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  useEffect(() => {
    // Don't start monitoring if it's been too long or export is complete
    if (!isMonitoringActive) {
      return;
    }

    const fetchStatus = async (): Promise<BackgroundExportStatus | null> => {
      try {
        const { data, error } = await supabase
          .from('csv_exports')
          .select('*')
          .eq('export_id', exportId)
          .maybeSingle();

        if (error) {
          setError(error.message);
          return null;
        }

        if (!data) {
          setError('Export not found - may have been cleaned up or expired');
          setIsMonitoringActive(false);
          return null;
        }

        const exportStatus: BackgroundExportStatus = {
          ...data,
          status: data.status as ExportStatus,
          filters: data.filters as Array<{ column: string; value: string; operation: string }> | undefined
        };

        // Calculate processing duration if still running
        if (exportStatus.status === 'processing' && exportStatus.started_at && !exportStatus.processing_duration_ms) {
          const startTime = new Date(exportStatus.started_at).getTime();
          const now = Date.now();
          exportStatus.processing_duration_ms = now - startTime;
        }

        setStatus(exportStatus);
        setError(null);

        if (onStatusChangeRef.current) {
          onStatusChangeRef.current(exportStatus);
        }

        // Stop monitoring if export is failed, or completed with download_url
        if (exportStatus.status === 'failed' ||
          (exportStatus.status === 'completed' && exportStatus.download_url)) {
          setIsMonitoringActive(false);
        }

        return exportStatus;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        return null;
      }
    };

    const setupSharedConnection = () => {
      const existing = connectionRegistry.get(exportId);

      if (existing) {
        // Use existing connection
        existing.count++;
        if (onStatusChangeRef.current) {
          existing.callbacks.push(onStatusChangeRef.current);
        }
        setIsConnected(true);
        return;
      }

      // Create new shared connection
      const channel = supabase
        .channel(`export-${exportId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'csv_exports',
            filter: `export_id=eq.${exportId}`
          },
          (payload) => {

            if (payload.new) {
              const exportStatus = payload.new as BackgroundExportStatus;

              // Calculate processing duration if still running
              if ((exportStatus.status === 'processing' || exportStatus.status === 'combining') && exportStatus.started_at && !exportStatus.processing_duration_ms) {
                const startTime = new Date(exportStatus.started_at).getTime();
                const now = Date.now();
                exportStatus.processing_duration_ms = now - startTime;
              }

              setStatus(exportStatus);
              setError(null);

              // Notify all callbacks for this export
              const connection = connectionRegistry.get(exportId);
              if (connection) {
                connection.callbacks.forEach(callback => {
                  try {
                    callback(exportStatus);
                  } catch (error) {
                    console.error('Error in export status callback:', error);
                  }
                });

                // Clean up connection if export is failed or completed with download_url
                if (exportStatus.status === 'failed' ||
                  (exportStatus.status === 'completed' && exportStatus.download_url)) {

                  // Add a small delay to ensure UI has processed the update
                  setTimeout(() => {
                    setIsMonitoringActive(false);
                    supabase.removeChannel(connection.channel);
                    connectionRegistry.delete(exportId);
                  }, 2000); // 2 second delay
                } else if (exportStatus.status === 'completed' && !exportStatus.download_url) {
                  console.warn(`Export ${exportId} marked as completed but missing download_url - continuing to monitor`);
                }
              }
            }
          }
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            setIsConnected(true);

            // Clear any existing polling interval
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = undefined;
            }
          } else if (status === 'CHANNEL_ERROR') {
            setIsConnected(false);
            console.warn(`Realtime connection failed for export ${exportId}, falling back to polling`);

            // Start polling as fallback, but limit duration
            if (!pollIntervalRef.current) {
              pollIntervalRef.current = setInterval(() => {
                // Check if we've been monitoring too long
                if (Date.now() - startTimeRef.current > maxMonitoringDuration) {
                  setIsMonitoringActive(false);
                  if (pollIntervalRef.current) {
                    clearInterval(pollIntervalRef.current);
                    pollIntervalRef.current = undefined;
                  }
                  return;
                }
                fetchStatus();
              }, fallbackPollingInterval);
            }
          }
        });

      // Register the connection
      connectionRegistry.set(exportId, {
        count: 1,
        channel,
        callbacks: onStatusChangeRef.current ? [onStatusChangeRef.current] : []
      });
    };

    // Initial fetch
    fetchStatus().then((initialStatus) => {
      // Only setup realtime if export is still active or completed without download_url
      if (initialStatus && (
        initialStatus.status === 'queued' ||
        initialStatus.status === 'processing' ||
        initialStatus.status === 'combining' ||
        (initialStatus.status === 'completed' && !initialStatus.download_url)
      )) {
        setupSharedConnection();
      } else {
        setIsMonitoringActive(false);
      }
    });

    // Timeout monitoring after max duration
    const timeoutId = setTimeout(() => {
      setIsMonitoringActive(false);
    }, maxMonitoringDuration);

    return () => {
      clearTimeout(timeoutId);

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = undefined;
      }

      // Decrease connection count or remove
      const existing = connectionRegistry.get(exportId);
      if (existing) {
        existing.count--;

        // Remove callback
        if (onStatusChange) {
          const index = existing.callbacks.indexOf(onStatusChange);
          if (index > -1) {
            existing.callbacks.splice(index, 1);
          }
        }

        // Clean up connection if no more users
        if (existing.count <= 0) {
          supabase.removeChannel(existing.channel);
          connectionRegistry.delete(exportId);
        }
      }
    };
  }, [exportId, fallbackPollingInterval, maxMonitoringDuration, isMonitoringActive]);

  return {
    status,
    isConnected,
    error,
    isMonitoringActive,
    refetch: async () => {
      if (!isMonitoringActive) {
        return;
      }

      const { data, error } = await supabase
        .from('csv_exports')
        .select('*')
        .eq('export_id', exportId)
        .maybeSingle();

      if (!error && data) {
        const exportStatus: BackgroundExportStatus = {
          ...data,
          status: data.status as ExportStatus,
          filters: data.filters as Array<{ column: string; value: string; operation: string }> | undefined
        };
        setStatus(exportStatus);

        // Stop monitoring if failed or completed with download_url
        if (exportStatus.status === 'failed' ||
          (exportStatus.status === 'completed' && exportStatus.download_url)) {
          setIsMonitoringActive(false);
        }
      }
    },
    stopMonitoring: () => setIsMonitoringActive(false)
  };
};